import agent, { AgentInfo } from '@egjs/agent';

let agentInfo: AgentInfo | null = null;

function getDeviceInfo() {
  if (agentInfo == null && typeof window !== 'undefined') {
    agentInfo = agent(window.navigator.userAgent);
  }

  return {
    browserName: agentInfo?.browser.name,
    browserVersion: agentInfo?.browser.version,
    isWebKit: agentInfo?.browser.webkit,
    os: agentInfo?.os.name,
    osVersion: agentInfo?.os.version,
    osMajorVersion: agentInfo?.os.majorVersion,
    isMobile: agentInfo?.isMobile,
    device_model: (typeof window !== 'undefined' && window.navigator.userAgentData?.model) ?? '',
  };
}

export default getDeviceInfo;

export function isPwaMode(): boolean {
  if (typeof window === 'undefined') return false;

  /**
   * NOTE: iPad와 Mac을 UserAgent로 구분하기 어렵기 때문에
   * 단순히 deviceInfo.isMobile로 체크하지 않고 아래와 같이 로직을 구성함
   */

  const deviceInfo = getDeviceInfo();
  // NOTE: 일부 터치가 지원되는 윈도우 노트북에서 fullscreen 모드 사용하는 케이스 제외
  // @egjs/agent 에서 'windows'를 'window'로 매핑하고 있음
  if (deviceInfo.os?.toLowerCase() === 'window' && !deviceInfo.isMobile) return false;

  return (
    // NOTE: safari 특정 버전에서는 (display-mode: standalone) 지원하지 않을 수 있음. 대신 navigator.standalone 프로퍼티를 사용
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window.navigator as any).standalone === true ||
    window.matchMedia('(display-mode: standalone)').matches ||
    // NOTE: 과거 manifest.json 에 display: fullscreen 으로 되어있었던 부분을 대응하기 위해 display-mode: fullscreen을 체크.
    (window.matchMedia('(display-mode: fullscreen)').matches && window.navigator.maxTouchPoints > 0)
  );
}

/** PWA 접속 시 'standalone', 일반 브라우저 접속 시 'browser' 반환. SSR 시 undefined */
export function getWebDisplayMode(): 'standalone' | 'browser' | undefined {
  if (typeof window === 'undefined') return undefined;
  return isPwaMode() ? 'standalone' : 'browser';
}

/** 브라우저 기기 OS. 예: iOS, Android. @egjs/agent는 ios/android 등 소문자 반환. SSR 시 undefined */
export function getWebBrowserOs(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const os = getDeviceInfo().os;
  if (!os) return undefined;
  const lower = os.toLowerCase();
  if (lower === 'ios') return 'iOS';
  if (lower === 'android') return 'Android';
  return os;
}

/**
 * iOS Safari 또는 macOS Safari인지 판별.
 * iOS의 모든 브라우저는 WebKit 기반이므로 iOS이면 무조건 true.
 * macOS에서는 Safari만 해당 (Chrome/Firefox 등은 자체 엔진 사용).
 */
export function isSafariOrIOSBrowser(): boolean {
  if (typeof navigator === 'undefined') return false;

  const { os, browserName } = getDeviceInfo();
  const osLower = os?.toLowerCase() ?? '';

  if (osLower === 'ios') return true;

  if (osLower === 'mac' || osLower === 'macos') {
    return browserName?.toLowerCase() === 'safari';
  }

  return false;
}

export function isChromeBrowser(): boolean {
  if (typeof navigator === 'undefined') return false;

  const { browserName } = getDeviceInfo();
  return browserName?.toLowerCase() === 'chrome';
}
